import { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Switch,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Text,
  Input as ChakraInput,
  Select as ChakraSelect,
} from "@chakra-ui/react";
import { batchAssessment, addProfileToList, getListProfile, updateListProfile, removeProfileFromList } from "@/services/lists-service";
import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";
import { IProfile, IBatchAssessment, IListProfile, formatDate, formatWithCallback, useAuth } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { MdEdit, MdSend } from "react-icons/md";
import { AddIcon } from "@chakra-ui/icons";
import { getPerson, updatePerson } from "../people/people-service";
import { PhoneInput } from "@bemap/ui";

const ViewListPage = () => {
  const { listId } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();

  const initialList: IListProfile = {
    id: 0,
    name: '',
    team_id: user?.currentTeamId || 0,
    profiles: []
  };

  const initialBatchAssessments: IBatchAssessment = {
    report_id: 0,
    client_can_unlock: false
  };

  const initialPerson: IProfile = {
    id: 0,
    name: '',
    email: '',
    phone: '',
    team_id: user?.currentTeamId || 0
  };

  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [list, setList] = useState<IListProfile>(initialList);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updateData, setUpdateData] = useState<number>();
  const [modalType, setModalType] = useState<"edit" | "send" | "delete" | "add_person" | "delete_person" | "edit_person">();
  const [batchAssessments, setBatchAssessments] = useState<IBatchAssessment>(initialBatchAssessments);
  const [person, setPerson] = useState<IProfile>(initialPerson);

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  async function loadList() {
    setIsLoading(true);
    try {
      const response = await getListProfile(parseInt(listId || '0'));
      setList(response.message);
      setIsLoading(false);
    } catch (error) {
      setList(initialList);
      setIsLoading(false);
    }
  }

  const handleUpdateListProfile = async () => {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    });
    setIsSaving(true);
    try {
      await updateListProfile(list);
      setIsSaving(false);
      setUpdateData(Date.now());
      toast.closeAll();
      closeModal();
    } catch (error) {
      setIsSaving(false);
      console.error(error);
    }
  };

  const submitBatchAssessments = async () => {
    try {
      setIsSaving(true);
      toast({
        title: t('general.saving'),
        status: 'loading',
        isClosable: true,
      });
      await batchAssessment(parseInt(listId || '0'), batchAssessments);
      setUpdateData(Date.now());
      toast.closeAll();
      toast({
        title: t('analyst.groups_sent_assessments_title'),
        description: t('analyst.groups_sent_assessments_text'),
        status: 'success',
        isClosable: true,
      });
      closeModal();
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [updateData]);

  const columns = useMemo<ColumnDef<IProfile, any>[]>(
    () => [
      {
        accessorFn: (row) => row?.name,
        id: "Nome",
        header: t('analyst.groups_table_name'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.email,
        id: "E-mail",
        header: "E-MAIL",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.id,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <TableRowActions
              editAction={() => editPerson(row.getValue())}
              deleteAction={() => openDeleteDialog(row.getValue())}
            />
          );
        },
      },
    ],
    [t]
  );

  function openModal(type: 'send' | 'edit' | 'delete' | 'add_person') {
    onOpen();
    setModalType(type);
  }

  function closeModal() {
    onClose();
    setPerson(initialPerson);
  }

  async function handleChange(event: any) {
    setList({
      ...list,
      [event.target.name]: event.target.value,
    });
  }

  async function handleChangeBatch(event: any) {
    setBatchAssessments({
      ...batchAssessments,
      [event.target.name]: event.target.value,
    });
  }

  async function editPerson(personId: number) {
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      });
      const req = await getPerson(personId);
      setPerson(req.message);
      setIsLoading(false);
      setModalType("edit_person");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    } finally {
      toast.closeAll();
    }
  }

  async function openDeleteDialog(personId: number) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    });
    try {
      const req = await getPerson(personId);

      toast.closeAll();
      setPerson(req.message);
      setModalType("delete_person");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  const handleCreatePerson = async (payload: IProfile) => {
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      });
      await addProfileToList(Number(listId), payload);
      setUpdateData(Date.now());
      toast.closeAll();
      closeModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdatePerson = async (payload: IProfile) => {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    });
    try {
      await updatePerson(payload, Number(listId));
      setUpdateData(Date.now());
      closeModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePerson = async () => {
    toast({
      title: t('general.deleting'),
      status: 'loading',
      isClosable: true,
    });
    try {
      if (person.id) {
        await removeProfileFromList(Number(listId), person.id);
        closeModal();
        setUpdateData(Date.now());
      }
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  async function handleChangePerson(event: any) {
    setPerson({
      ...person,
      [event.target.name]: event.target.value,
    });
  }

  const InfoRow = ({ title, text, isLoading = false }: { title: string; text: string; isLoading?: boolean }) => {
    const titleColor = useColorModeValue('gray.900', 'white');
    const textColor = useColorModeValue('gray.600', 'gray.50');

    return (
      <Box flexGrow={"1"}>
        <Text fontSize="1.1rem" fontWeight={'bold'} color={titleColor}>{title}</Text>
        <Skeleton minHeight="20px" isLoaded={!isLoading}>
          <Text color={textColor}>{text || ""}</Text>
        </Skeleton>
      </Box>
    );
  };

  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.groups_edit')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.groups_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder="Ex: Formação 2024"
                      onChange={handleChange}
                      value={list.name || ""}
                      required
                    />
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleUpdateListProfile} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "add_person":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.groups_add_person')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.people_new_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      onChange={handleChangePerson}
                      value={person?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="email"
                      placeholder={t('general.placeholder_email')}
                      type="email"
                      onChange={handleChangePerson}
                      value={person?.email || ""}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>Phone</FormLabel>
                    <PhoneInput
                      value={person?.phone || ""}
                      onChange={(phone) => setPerson({ ...person, phone })}
                    />
                  </FormControl>
                  <Accordion allowToggle mt={4}>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          {t('actions.additional_info')}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <FormControl id="company">
                          <FormLabel>{t('analyst.company')}</FormLabel>
                          <ChakraInput
                            type="text"
                            placeholder={t('general.placeholder_company')}
                            onChange={(e) => {
                              setPerson((prev) => ({
                                ...prev,
                                company: {
                                  name: e.target.value
                                },
                              }));
                            }}
                          />
                        </FormControl>
                        <FormControl id="birth" mt={4}>
                          <FormLabel>{t('analyst.people_birth_date')}</FormLabel>
                          <ChakraInput
                            type="date"
                            onChange={(e) => {
                              setPerson((prev) => ({
                                ...prev,
                                metadata: { ...prev.metadata, birth: new Date(e.target.value).toISOString() },
                              }));
                            }}
                          />
                        </FormControl>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={() => handleCreatePerson(person)} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "send":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.groups_send')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"} gap="20px">
                  <FormControl>
                    <FormLabel>{t('analyst.assesments_new_type')}</FormLabel>
                    <ChakraSelect
                      color={textColor}
                      background={background}
                      name="report_id"
                      defaultValue="default"
                      onChange={handleChangeBatch}
                    >
                      <option value="default" disabled hidden>
                        {t('actions.click_to_select')}
                      </option>
                      <option value="1">Extended Report</option>
                      <option value="2">Simple Report</option>
                    </ChakraSelect>
                  </FormControl>
                  <FormControl display='flex' alignItems='center' gap="20px">
                    <Switch
                      id='client_can_unlock'
                      colorScheme={'blue'}
                      onChange={(e) => setBatchAssessments({ ...batchAssessments, client_can_unlock: e.target.checked })}
                    />
                    <FormLabel htmlFor='client_can_unlock' mb='0'>
                      {t('analyst.assesments_new_unlock')}
                    </FormLabel>
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  isDisabled={batchAssessments.report_id === 0}
                  onClick={submitBatchAssessments}
                  isLoading={isSaving}
                >
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "edit_person":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.people_edit')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.people_new_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      onChange={handleChangePerson}
                      value={person?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="email"
                      placeholder={t('general.placeholder_email')}
                      type="email"
                      onChange={handleChangePerson}
                      value={person?.email || ""}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>Phone</FormLabel>
                    <PhoneInput
                      value={person?.phone || ""}
                      onChange={(phone) => setPerson({ ...person, phone })}
                    />
                  </FormControl>
                  <Accordion allowToggle mt={4}>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          {t('actions.additional_info')}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <FormControl id="company">
                          <FormLabel>{t('analyst.company')}</FormLabel>
                          <ChakraInput
                            type="text"
                            defaultValue={person?.listProfiles?.find(l => l.list_profile_id === Number(listId))?.company?.name || ""}
                            placeholder={t('general.placeholder_company')}
                            onChange={(e) => {
                              setPerson((prev) => ({
                                ...prev,
                                company: {
                                  name: e.target.value
                                },
                              }));
                            }}
                          />
                        </FormControl>
                        <FormControl id="birth" mt={4}>
                          <FormLabel>{t('analyst.people_birth_date')}</FormLabel>
                          <ChakraInput
                            type="date"
                            value={formatWithCallback(person?.metadata?.birth, 'yyyy-MM-dd', -7)}
                            onChange={(e) => {
                              setPerson((prev) => ({
                                ...prev,
                                metadata: { ...prev.metadata, birth: new Date(e.target.value).toISOString() },
                              }));
                            }}
                          />
                        </FormControl>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdatePerson(person)}
                  isLoading={isSaving}
                >
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete_person':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('analyst.groups_remove_person')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeletePerson} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
    }
  }

  return (
    <>
      <VStack gap="20px" w="100%" align="stretch">
        <PageHeading title={t('analyst.groups')} actions={[
          {
            label: t('analyst.groups_send'),
            icon: <MdSend />,
            onClick: () => openModal('send')
          },
          {
            label: t('analyst.groups_add_person'),
            icon: <AddIcon />,
            variant: 'outline',
            onClick: () => openModal('add_person')
          },
          {
            label: t('analyst.groups_edit'),
            icon: <MdEdit />,
            variant: 'outline',
            onClick: () => openModal('edit')
          }
        ]} />
        <Panel heading={t('analyst.groups_info')} padding={4}>
          <Flex gap={4}>
            <InfoRow isLoading={isLoading} title={t('analyst.groups_name')} text={list?.name || ''} />
            <InfoRow isLoading={isLoading} title={t('analyst.groups_created')} text={formatDate(list?.created_at || '', true)} />
          </Flex>
        </Panel>

        <Panel heading={t('partner.people')}>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            list && (
              <Table
                tableId="clients-view-list-people-table"
                columns={columns}
                data={list.profiles || []}
              />
            )
          )}
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default ViewListPage; 